import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, ChevronDown, X, Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * Autocomplete genérico con búsqueda en tiempo real.
 *
 * Props:
 *  - fetchOptions(q): async function que recibe el query y devuelve un array de
 *     items. Si devuelve {data: [...]} también lo aceptamos.
 *  - value: id seleccionado actualmente (string | null).
 *  - onChange(id, item | null): callback al seleccionar o limpiar.
 *  - placeholder, disabled, error.
 *  - getOptionLabel(item): cómo se muestra el item (default: item.label).
 *  - getOptionKey(item): key único (default: item._id).
 *  - getOptionId(item): el id a guardar (default: item._id).
 *  - emptyText: texto cuando no hay resultados.
 *  - minChars: mínimo de caracteres para disparar fetch (default: 1).
 *  - debounceMs: tiempo de debounce (default: 250).
 */
const Autocomplete = ({
  fetchOptions,
  value,
  onChange,
  placeholder = 'Buscar…',
  disabled = false,
  error = null,
  getOptionLabel = (item) => item.label || item.nombre || item.razonSocial || String(item),
  getOptionKey = (item) => item._id || item.id,
  getOptionId = (item) => item._id || item.id,
  emptyText = 'Sin resultados',
  minChars = 1,
  debounceMs = 250,
  className,
  required = false,
}) => {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState('');
  const debounceRef = useRef(null);
  const containerRef = useRef(null);
  const lastFetchId = useRef(0);

  // Si hay un valor externo (modo edición), reflejarlo en el label
  useEffect(() => {
    if (value && !selectedLabel) {
      // El padre debe pasar el item en un map aparte o fetchearlo. Por ahora
      // dejamos el label vacío hasta que se edite; alternativa: pasar `initialLabel`.
    }
  }, [value, selectedLabel]);

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const doFetch = useCallback(
    async (q) => {
      if (q.length < minChars) {
        setOptions([]);
        return;
      }
      const fetchId = ++lastFetchId.current;
      setLoading(true);
      try {
        const result = await fetchOptions(q);
        // Si mientras fetché el usuario escribió otra cosa, descartamos
        const data = Array.isArray(result) ? result : result?.data || [];
        if (fetchId === lastFetchId.current) {
          setOptions(data);
          setIsOpen(true);
        }
      } catch (e) {
        if (fetchId === lastFetchId.current) {
          setOptions([]);
        }
      } finally {
        if (fetchId === lastFetchId.current) {
          setLoading(false);
        }
      }
    },
    [fetchOptions, minChars],
  );

  const handleQueryChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doFetch(v), debounceMs);
  };

  const handleSelect = (item) => {
    const id = getOptionId(item);
    const label = getOptionLabel(item);
    setSelectedLabel(label);
    setQuery(label);
    setIsOpen(false);
    onChange?.(id, item);
  };

  const handleClear = () => {
    setQuery('');
    setSelectedLabel('');
    setOptions([]);
    onChange?.(null, null);
  };

  return (
    <div ref={containerRef} className={cn('relative w-full', className)}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={handleQueryChange}
          onFocus={() => {
            if (options.length > 0) setIsOpen(true);
          }}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          className={cn(
            'flex h-10 w-full rounded-md border bg-background pl-9 pr-9 py-2 text-sm',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            'disabled:cursor-not-allowed disabled:opacity-50',
            error ? 'border-destructive' : 'border-input',
          )}
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
        )}
        {!loading && query && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-accent"
            aria-label="Limpiar"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full max-h-72 overflow-y-auto rounded-md border border-border bg-popover text-popover-foreground shadow-lg">
          {loading && options.length === 0 ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">Buscando…</div>
          ) : options.length === 0 ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">{emptyText}</div>
          ) : (
            <ul className="py-1">
              {options.map((item) => (
                <li key={getOptionKey(item)}>
                  <button
                    type="button"
                    onClick={() => handleSelect(item)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none"
                  >
                    {getOptionLabel(item)}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default Autocomplete;
